import moment from 'moment';
import {
  MeasureHeightAction,
  MeasureHeightActionTypes,
  MeasureHeightState,
} from '../../types/measure-height';
import { actions } from '../index';

import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';
import { SenseiApi } from '../../utils/api';

import createRxStore, { rxDispatch, ofType } from '..';
import {
  fetchHomeDpuSearchResults,
  fetchHomeOrganizationSearchResults,
} from '../../rx-actions/load-home';
import { getRequiredSessionToken } from '../../utils/session-token';

import {
  ResourceStatus,
  RESOURCE_LOADING,
  RESOURCE_IDLE,
} from '../../types/resource';

export const initialState: MeasureHeightState = {
  open: false,
  resource: RESOURCE_IDLE,
};

function reducer(
  state: MeasureHeightState,
  action: MeasureHeightAction,
): MeasureHeightState {
  switch (action.type) {
    case MeasureHeightActionTypes.MEASURE_HEIGHT_OPEN:
      return { ...state, open: true };
    case MeasureHeightActionTypes.MEASURE_HEIGHT_CLOSE:
      return initialState;

    case MeasureHeightActionTypes.MEASURE_HEIGHT_START:
      return { ...state, resource: RESOURCE_LOADING };

    case MeasureHeightActionTypes.MEASURE_HEIGHT_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            height: `${action.height}`,
            stddev: action.stddev,
          },
        },
      };

    case MeasureHeightActionTypes.MEASURE_HEIGHT_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.ERROR,
          error: action.error,
        },
      };

    default:
      return state;
  }
}

const MeasureHeightStore = createRxStore(
  'MeasureHeightStore',
  initialState,
  reducer,
);

// =================================
// SIDE EFFECT: When "measure height" is clicked, start the measure height process
// =================================
const MEASURE_HEIGHT_POLL_INTERVAL_MILLISECONDS = 2000;
const MEASURE_HEIGHT_POLL_LIMIT_MILLISECONDS = 120000;

let pollIntervalId: number | null = null;
const stopMeasureHeight = () => {
  if (pollIntervalId === null) {
    return;
  }
  clearInterval(pollIntervalId);
  pollIntervalId = null;
};

actions
  .pipe(
    ofType<{ serialNumber: string }>(
      MeasureHeightActionTypes.MEASURE_HEIGHT_START,
    ),
  )
  .subscribe(async (action) => {
    const serialNumber = action.serialNumber;
    const token = getRequiredSessionToken();

    let measureHeightResponse;
    try {
      measureHeightResponse = await SenseiApi.measureHeight(
        token,
        action.serialNumber,
      );
    } catch (error) {
      rxDispatch({
        type: MeasureHeightActionTypes.MEASURE_HEIGHT_ERROR,
        error,
      });
    }
    const commandId = measureHeightResponse.command_id;

    const pollingStartedAt = performance.now();
    pollIntervalId = window.setInterval(async () => {
      // Verify that the max time limit hasn't been exceeded
      const now = performance.now();
      if (now - pollingStartedAt > MEASURE_HEIGHT_POLL_LIMIT_MILLISECONDS) {
        stopMeasureHeight();
        rxDispatch({
          type: MeasureHeightActionTypes.MEASURE_HEIGHT_ERROR,
          error: new Error(
            `Measure height operation timed out - is the sensor offline?`,
          ),
        });
        return;
      }

      // Get a list of recent commands
      let commandListResponse;
      try {
        commandListResponse = await SenseiApi.listCommands(token, serialNumber);
      } catch (error) {
        stopMeasureHeight();
        rxDispatch({
          type: MeasureHeightActionTypes.MEASURE_HEIGHT_ERROR,
          error,
        });
        return;
      }
      const commands: Array<{
        command_id: string;
        return_status: {
          result: string;
          output: string;
        } | null;
      }> = commandListResponse.results;

      // Find the command that was just kicked off
      const command = commands.find((i) => i.command_id === commandId);
      if (!command) {
        return;
      }

      // Ensure the command has completed.
      if (command.return_status === null) {
        return;
      }

      // Ensure the command has completed successfully.
      if (command.return_status.result !== 'OK') {
        stopMeasureHeight();
        rxDispatch({
          type: MeasureHeightActionTypes.MEASURE_HEIGHT_ERROR,
          error: new Error(
            `Measure height command failed: ${JSON.stringify(
              command.return_status,
            )}`,
          ),
        });
        return;
      }

      const [heightString, stddevString] =
        command.return_status.output.split(':');

      stopMeasureHeight();
      rxDispatch({
        type: MeasureHeightActionTypes.MEASURE_HEIGHT_COMPLETE,
        height: parseFloat(heightString),
        stddev: parseFloat(stddevString),
      });
    }, MEASURE_HEIGHT_POLL_INTERVAL_MILLISECONDS);
  });

// =================================
// SIDE EFFECT: When the measure height popup is closed, stop the in progress measure height
// processes if one exists
// =================================
actions
  .pipe(ofType(MeasureHeightActionTypes.MEASURE_HEIGHT_CLOSE))
  .subscribe(() => stopMeasureHeight());

export default MeasureHeightStore;
